/**
 * Pure projection of a raw tool result into renderable entity chips.
 *
 * Every function here is best-effort and returns null on anything unexpected,
 * so the caller falls back to the generic JSON view rather than rendering a
 * half-built card. No React, no DOM — this is the testable half of the result
 * cards.
 */

import type { EntityKind } from './entity-kinds';

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

export interface EntityView {
  id: string | null;
  primary: string;
  secondary: string | null;
}

/** Max chips rendered before the "+n more" tail. */
export const MAX_CHIPS = 8;

/** Money-ish formatting for deal values. */
export function formatValue(value: unknown): string | null {
  const num =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  if (!Number.isFinite(num) || num === 0) return null;
  return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

/** Secondary line for one entity kind — the sub-label under the chip title. */
function toSecondary(record: Record<string, unknown>, kind: EntityKind): string | null {
  switch (kind) {
    case 'contact':
      return asString(record.title) ?? asString(record.email) ?? asString(record.company_name);
    case 'company':
      return asString(record.industry) ?? asString(record.domain) ?? asString(record.website);
    case 'deal': {
      const value = formatValue(record.value ?? record.amount ?? record.deal_value);
      const stage =
        asString(record.stage) ?? asString(record.stage_name) ?? asString(record.status);
      return [value, stage].filter(Boolean).join(' · ') || null;
    }
    case 'task':
      return asString(record.due_date) ?? asString(record.status);
    case 'ticket':
      return asString(record.status) ?? asString(record.priority);
  }
}

/** Best-effort projection of one raw entity record into chip fields. */
export function toEntityView(
  record: Record<string, unknown>,
  kind: EntityKind
): EntityView | null {
  const id = asString(record.id) ?? asString(record[`${kind}_id`]);

  const first = asString(record.first_name) ?? asString(record.firstName);
  const last = asString(record.last_name) ?? asString(record.lastName);
  const fullName = first ? (last ? `${first} ${last}` : first) : null;

  const primary =
    fullName ??
    asString(record.name) ??
    asString(record.full_name) ??
    asString(record.title) ??
    asString(record.subject) ??
    asString(record.company_name) ??
    asString(record.email);
  if (!primary) return null;

  return { id, primary, secondary: toSecondary(record, kind) };
}

/** Pull the array of entity records out of common result envelopes. */
export function extractItems(output: unknown): Record<string, unknown>[] | null {
  let raw: unknown = null;
  if (Array.isArray(output)) raw = output;
  else if (isRecord(output)) {
    raw =
      (Array.isArray(output.items) && output.items) ||
      (Array.isArray(output.results) && output.results) ||
      (Array.isArray(output.contacts) && output.contacts) ||
      (Array.isArray(output.companies) && output.companies) ||
      (Array.isArray(output.deals) && output.deals) ||
      (Array.isArray(output.tasks) && output.tasks) ||
      (Array.isArray(output.tickets) && output.tickets) ||
      null;
    // Single-entity result ({ contact: {...} } or a bare record with an id).
    if (!raw) {
      for (const key of ['contact', 'company', 'deal', 'task', 'ticket'] as const) {
        const nested = output[key];
        if (isRecord(nested)) return [nested];
      }
      if (
        'id' in output &&
        (asString(output.name) ||
          asString(output.first_name) ||
          asString(output.title) ||
          asString(output.subject))
      ) {
        return [output];
      }
    }
  }
  if (!Array.isArray(raw)) return null;
  const records = raw.filter(isRecord);
  return records.length > 0 ? records : null;
}

/** Project raw output → renderable chip views; null when nothing matches. */
export function buildEntityViews(output: unknown, kind: EntityKind): EntityView[] | null {
  const items = extractItems(output);
  if (!items) return null;
  const views = items
    .map((r) => toEntityView(r, kind))
    .filter((v): v is EntityView => v !== null);
  return views.length > 0 ? views : null;
}
